// `(columns ds [:a :b …])` — project a Dataset to the listed columns, in
// that order. Top-level eager op consumable as `(columns ds [:a :b])`
// directly or via `(-> ds (columns [:a :b]))` threading.

import { projectSchema } from '../../schema/types.ts';
import { type Cell, MaterializedDataset } from '../../value.ts';
import { buildDsColumnListOp } from './helpers.ts';

/**
 * `(columns ds [:name :other …])` — project `ds` to the listed
 * columns, preserving the order in which they appear in the vector.
 * Each name is a keyword (typical) or a string (special-character
 * fallback). Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - A listed name isn't a column on `ds.schema` →
 *     `UnknownColumnRefError` (with the dataset's columns as the
 *     "did you mean").
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * projection runs — `columns` produces a `MaterializedDataset` either
 * way.
 */
export const columns = buildDsColumnListOp({
  name: 'columns',
  describe:
    'Project a Dataset to the listed columns, in vector order. Names are keywords (typical) ' +
    'or strings (arbitrary characters). Composes with threading: `(-> ds (columns [:a :b]))`.',
  example: { expr: '(-> wells (columns [:name :region :revenue]))' },
  // Row set unchanged — only the projected columns differ. `projectSchema`
  // retains the instance-identity columns (`space` / `externalId`) the source
  // carries even when `names` omits them, so a downstream cross-view set-ref
  // can still bind them; build the data from the projected schema's columns so
  // the column set and schema stay aligned. The identity axis passes through
  // verbatim.
  verb: (ds, names) => {
    const schema = projectSchema(ds.schema, names);
    return new MaterializedDataset(
      Object.fromEntries(Array.from(schema.keys()).map((n) => [n, (ds.columns[n] ?? []) as readonly Cell[]])),
      schema,
      ds.identity,
    );
  },
  propagateSchema: (names, ctx) => projectSchema(ctx.inputSchema, names),
});
